// Reading a block, on the client.
//
// Pure functions only — the server owns every number here; this decides how it
// reads. It lives outside the component so the formatting rules can be tested
// directly, the same reasoning as setEntry.js.
//
// The rule running through all of it: **an empty cell and a zero are different
// things, and must not render the same.** A week nobody trained has no top set;
// a bodyweight set has a top set of 0. Collapsing them makes an untrained week
// look like a session of empty-bar work.

/** The three roles a rest period can play. Derived server-side; see routes/grid.go. */
export const REST_ROLE = {
  TRANSITION: 'transition',
  BETWEEN_GROUPS: 'between-groups',
  BETWEEN_SETS: 'between-sets',
};

/**
 * What a rest period is FOR, in words.
 *
 * The same "90" means three different things depending on what follows the
 * exercise, and rendering it as a bare number leaves the athlete to guess. After
 * the first half of a superset it is a walk to the next station; after the last
 * exercise of the group it is the recovery that makes the next round possible.
 */
export function restLabel(seconds, role) {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value < 0) return '';

  const time = formatSeconds(value);
  switch (role) {
    case REST_ROLE.TRANSITION:
      return `${time} → next`;
    case REST_ROLE.BETWEEN_GROUPS:
      return `${time} recovery`;
    case REST_ROLE.BETWEEN_SETS:
      return `${time} between sets`;
    default:
      return time;
  }
}

function formatSeconds(value) {
  if (value === 0) return 'none';
  if (value < 60) return `${value}s`;
  const minutes = Math.floor(value / 60);
  const seconds = value % 60;
  return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
}

/**
 * A one-line summary of what happened in a cell.
 *
 * @param {{setsPerformed?: number, topSet?: number|null, sets?: Array}} cell
 * @param {string} unit the reader's unit, for the load
 * @returns {string} e.g. "3 x 5 @ 110 kg", or "" for an untrained week
 */
export function cellSummary(cell, unit = 'kg') {
  if (!cell || !cell.setsPerformed) return '';

  const sets = cell.sets ?? [];
  const reps = sets.map((set) => set.repsPerformed);
  const uniformReps = reps.length > 0 && reps.every((r) => r === reps[0]);

  const volume = uniformReps
    ? `${sets.length} x ${reps[0]}`
    : `${sets.length} sets`;

  // topSet is null for an untrained week and 0 for bodyweight work — so the
  // check is against null, not falsiness. `if (!cell.topSet)` would drop the
  // load from every bodyweight cell.
  if (cell.topSet === null || cell.topSet === undefined) {
    return volume;
  }
  return `${volume} @ ${cell.topSet} ${unit}`;
}

/**
 * Whether a cell was trained at all. Used to style the empty ones.
 */
export function isCellEmpty(cell) {
  return !cell || !cell.setsPerformed;
}

/**
 * Adherence as a percentage, or null when nothing was prescribed.
 *
 * Null rather than 0: a block with no prescriptions has no adherence to report,
 * and "0%" reads as total failure rather than as an empty block.
 */
export function adherencePercent(performed, prescribed) {
  const total = Number(prescribed);
  if (!Number.isFinite(total) || total <= 0) return null;
  return Math.round((Number(performed) / total) * 100);
}

/**
 * The most recent week the athlete actually logged something in, or null.
 *
 * # WHY THE LATEST LOGGED WEEK AND NOT "THIS WEEK"
 *
 * The honest alternative is a date: which week of the block today falls in. That
 * needs the phase start date and the midnight-UTC calendar rule, and it can
 * disagree with the cells drawn above it — a user who trained Sunday night and
 * opens the app Monday morning would be shown an empty week beside a grid full
 * of their sets. Reading the week off the logged work cannot disagree with the
 * work, because it IS the work.
 *
 * The cost is a returning user: someone back after a fortnight away sees the
 * week they last trained rather than the week they are in. That is a real
 * limitation and the reason this is a first cut rather than the final answer.
 *
 * Null when nothing has been logged anywhere in the block — which is a different
 * state from "logged nothing this week", and the caller must not collapse them
 * into a 0%. See adherencePercent: "0%" reads as total failure.
 */
export function latestLoggedWeek(weekAdherence) {
  const weeks = Array.isArray(weekAdherence) ? weekAdherence : [];
  let latest = null;
  for (const week of weeks) {
    if (Number(week?.performedSets) > 0) latest = week;
  }
  return latest;
}

/**
 * How a block's progress on one exercise reads.
 *
 * @returns {{text: string, direction: 'up'|'down'|'flat'|'unknown'}}
 */
export function trendLabel(changeKg, changePercent, unit = 'kg') {
  // Null means "not enough data", which is not the same as no change. One
  // trained week is a point, not a trend.
  if (changeKg === null || changeKg === undefined) {
    return { text: '—', direction: 'unknown' };
  }

  const change = Number(changeKg);
  if (change === 0) return { text: 'no change', direction: 'flat' };

  const sign = change > 0 ? '+' : '−';
  const magnitude = Math.abs(round(change, 1));
  const direction = change > 0 ? 'up' : 'down';

  if (changePercent === null || changePercent === undefined) {
    return { text: `${sign}${magnitude} ${unit}`, direction };
  }
  const percent = Math.abs(round(Number(changePercent), 1));
  return { text: `${sign}${magnitude} ${unit} (${sign}${percent}%)`, direction };
}

function round(value, places) {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

/**
 * The label for a phase, combining its number and name.
 *
 * The strength quality is served spelled out beside its code, so the client never
 * carries the mapping — the same reasoning as a load beside its unit.
 */
export function phaseLabel(phase) {
  if (!phase) return '';
  const parts = [];
  if (phase.phaseNumber) parts.push(`Phase ${phase.phaseNumber}`);
  if (phase.name) parts.push(phase.name);

  const label = parts.join(' · ');
  const quality = phase.strengthQualityLabel;
  return quality ? `${label} — ${quality}` : label;
}

/**
 * The week numbers a block covers, for rendering columns.
 *
 * Falls back to deriving them from blockLengthWeeks so a caller holding only the
 * phase can render the header before the grid loads.
 */
export function weekColumns(phase, weeks) {
  if (Array.isArray(weeks) && weeks.length > 0) return weeks;

  const length = Number(phase?.blockLengthWeeks);
  if (!Number.isFinite(length) || length < 1) return [];
  return Array.from({ length: Math.floor(length) }, (_, i) => i + 1);
}

/**
 * What happened when a block was finished.
 *
 * # WHY THIS IS NOT explainEvent
 *
 * trainingData.js explains an outbox ROW to the athlete it is about, in the
 * past tense, on a screen listing many. This explains one RESPONSE to the coach
 * who just pressed the button, and the two say different things about the same
 * hold: the athlete needs "you had not turned sharing on", the coach needs to
 * know their block was recorded regardless.
 *
 * Recording the block and sharing it are separate outcomes. A held event is not
 * a failure — the count is the coach's either way — so the copy leads with what
 * was recorded and reports sharing second.
 *
 * @returns {{recorded: string, sharing: string}}
 */
export function finishOutcome(result) {
  if (!result) return { recorded: '', sharing: '' };

  const percent = adherencePercent(result.performedSets, result.prescribedSets);
  const figure = percent === null
    ? `${result.performedSets ?? 0} sets`
    : `${percent}% (${result.performedSets} of ${result.prescribedSets})`;

  const recorded = result.alreadyRecorded
    // The figure that was SENT is whatever it was at the time. Saying "recorded"
    // again would claim this newer number went upstream, and it did not.
    ? `This block was already finished. It now stands at ${figure}.`
    : `Block finished at ${figure}.`;

  switch (result.sharing) {
    case 'pending':
      return { recorded, sharing: 'Queued to share with the programme writer.' };
    case 'sent':
      return { recorded, sharing: 'Shared with the programme writer.' };
    case 'held':
      return {
        recorded,
        sharing: result.hold === 'no_consent'
          ? 'Not shared — sharing is turned off. You can turn it on in My Training.'
          : 'Not shared — this programme was not written by the model, so there is '
            + 'nothing to send it back to.',
      };
    case 'failed':
      return { recorded, sharing: 'Could not be shared. It will be retried.' };
    default:
      return { recorded, sharing: '' };
  }
}
